/**
 * 24-hour conversation memory for the chat widget.
 *
 * The transcript is kept ONLY in the visitor's own browser (localStorage),
 * scoped per embedding site, and expires 24 hours after the last activity. The
 * server stays stateless — history already rides along on each /api/ask request
 * — so this adds "pick up where you left off" without any server-side session
 * store, and without the widget ever setting a cookie. Everything is wrapped in
 * try/catch: if storage is unavailable (private mode, disabled, quota), the
 * widget silently falls back to in-memory-only and still works.
 *
 * Privacy: this is the visitor's own data on their own device. It never leaves
 * the browser; what the server persists is still only the redacted question and
 * answer. A visible "Clear conversation" control wipes it on demand, and it
 * self-expires at 24h.
 */

const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const KEY_PREFIX = 'inquiros_convo_';
const MAX_STORED_MESSAGES = 40; // cap so storage can't grow without bound

function storageKey(scope) {
  return KEY_PREFIX + (scope || 'default');
}

function available() {
  try {
    const k = '__inq_probe__';
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

export function loadConversation(scope) {
  if (!available()) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(scope));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data.savedAt !== 'number') return null;
    if (Date.now() - data.savedAt > TTL_MS) {
      window.localStorage.removeItem(storageKey(scope));
      return null;
    }
    return {
      messages: Array.isArray(data.messages) ? data.messages : [],
      conversationId: typeof data.conversationId === 'string' ? data.conversationId : null,
      audience: typeof data.audience === 'string' ? data.audience : null,
    };
  } catch {
    return null;
  }
}

export function saveConversation(scope, messages, conversationId, audience = null) {
  if (!available()) return;
  try {
    if ((!messages || !messages.length) && !audience) {
      window.localStorage.removeItem(storageKey(scope));
      return;
    }
    const data = {
      savedAt: Date.now(),
      conversationId: conversationId || null,
      audience,
      messages: (messages || []).slice(-MAX_STORED_MESSAGES),
    };
    window.localStorage.setItem(storageKey(scope), JSON.stringify(data));
  } catch {
    /* quota or serialization issue — keep running in memory */
  }
}

export function clearConversation(scope) {
  try {
    window.localStorage.removeItem(storageKey(scope));
  } catch {
    /* nothing to do */
  }
}
